/**
 * Formatters shared by every app. Money is integer IQD (never a float); dates and times are shown
 * in Asia/Baghdad whatever the device's own time zone. Digits are Latin (0–9) in both languages,
 * like receipts, phone numbers and the POS (D88).
 */
export type Lang = 'ar' | 'en';

export const TIME_ZONE = 'Asia/Baghdad';

export function locale(lang: Lang): string {
  return lang === 'ar' ? 'ar-IQ-u-nu-latn' : 'en-GB';
}

export function direction(lang: Lang): 'rtl' | 'ltr' {
  return lang === 'ar' ? 'rtl' : 'ltr';
}

/**
 * `1234567` → `1,234,567` for any integer, without Intl: Hermes (the phones' JavaScript engine)
 * cannot pass a BigInt to Intl.NumberFormat ("Cannot convert BigInt to number", found by the
 * Dob Staff emulator check, M8-0), and money is BigInt.
 */
function groupThousands(value: bigint): string {
  const negative = value < 0n;
  const digits = (negative ? -value : value).toString();
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return negative ? `-${grouped}` : grouped;
}

/** Whole dinars with thousands separators: `2,500,000 د.ع` / `IQD 2,500,000`. */
export function formatIqd(amount: bigint | number | string, lang: Lang): string {
  const value = typeof amount === 'bigint' ? amount : BigInt(amount);
  const digits = groupThousands(value);
  return lang === 'ar' ? `${digits} د.ع` : `IQD ${digits}`;
}

export function formatNumber(value: number | bigint, lang: Lang): string {
  if (typeof value === 'bigint') {
    // Exact as a number → the usual formatting; beyond that, Latin digits and commas.
    const safe =
      value <= BigInt(Number.MAX_SAFE_INTEGER) && value >= BigInt(Number.MIN_SAFE_INTEGER);
    if (!safe) return groupThousands(value);
    return new Intl.NumberFormat(locale(lang)).format(Number(value));
  }
  return new Intl.NumberFormat(locale(lang)).format(value);
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

/** A size for people: `950 B`, `12.3 MB`, `1.5 GB` (1 KB = 1024 B; Latin digits, unit in Latin; LTR-isolated). */
export function formatBytes(bytes: number): string {
  let value = Math.max(0, bytes);
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit++;
  }
  const shown =
    unit === 0 || value >= 100 ? String(Math.round(value)) : value.toFixed(1).replace(/\.0$/, '');
  // Isolated left-to-right (U+2066 … U+2069): inside Arabic text it stays "191 GB", never "GB 191".
  return `\u2066${shown} ${BYTE_UNITS[unit] ?? 'B'}\u2069`;
}

/** Basis points as a percentage: 500 → `5%`, 1250 → `12.5%`. */
export function formatBasisPoints(bp: number): string {
  const whole = Math.trunc(bp / 100);
  const rest = Math.abs(bp % 100);
  if (rest === 0) return `${whole}%`;
  return `${whole}.${String(rest).padStart(2, '0').replace(/0$/, '')}%`;
}

export function formatDateTime(iso: string | Date, lang: Lang): string {
  return new Intl.DateTimeFormat(locale(lang), {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

/** Baghdad wall-clock time, 24 hours: `04:00`. */
export function formatTime(iso: string | Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));
}

export function formatDate(iso: string | Date, lang: Lang): string {
  return new Intl.DateTimeFormat(locale(lang), {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(iso));
}

/** A day with its weekday and full month, Baghdad: `الأحد، 11 تشرين الأول 2026` / `Sunday, 11 October 2026`. */
export function formatDateLong(iso: string | Date, lang: Lang): string {
  return new Intl.DateTimeFormat(locale(lang), {
    timeZone: TIME_ZONE,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(iso));
}

/** A countdown: `4:05` below an hour, `1:04:05` above. Never negative. */
export function formatCountdown(milliseconds: number): string {
  const total = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const ss = String(seconds).padStart(2, '0');
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${ss}` : `${minutes}:${ss}`;
}

/** Arabic names of the cities we operate in; locations store the city in English. */
const CITIES_AR: Readonly<Record<string, string>> = {
  Kirkuk: 'كركوك',
  Mosul: 'الموصل',
  Tikrit: 'تكريت',
  Khanaqin: 'خانقين',
  Baghdad: 'بغداد',
  Erbil: 'أربيل',
  Sulaymaniyah: 'السليمانية',
};

export function cityName(city: string, lang: Lang): string {
  return lang === 'ar' ? (CITIES_AR[city] ?? city) : city;
}
